import React, { useState, useRef, useEffect } from 'react';
import Confetti from 'react-dom-confetti';
import { DatePicker } from '@mantine/dates';
import { Slider } from '@mantine/core';
import { TbPin } from 'react-icons/tb';
import { TbNotes } from 'react-icons/tb';
import { TbArrowUp, TbArrowDown, TbClockDown, TbRepeat } from 'react-icons/tb';
// import { IoPlayOutline, IoStopOutline } from 'react-icons/io5';
import './Task.css';
import { SharedUser, SharedBy } from '../App';

interface Category {
  id: string;
  name: string;
}

interface TaskProps {
  id: string;
  title: string;
  isCompleted: boolean;
  category: string;
  categories: Category[];
  urgency: 'high' | 'medium' | 'low' | null;
  urgencyNumber?: number;
  urgencyRank?: number | null;
  hasCustomPriority?: boolean;
  isUnranked?: boolean;
  willDoToday: 'today' | 'pinned' | false;
  selectedCategoryId: string;
  description?: string;
  isFirstInCategory: boolean;
  isLastInCategory: boolean;
  timerStartedAt?: number;
  totalElapsedTime?: number;
  sharedWith?: SharedUser[];
  sharedBy?: SharedBy;
  onDelete: (id: string) => void;
  onToggle: (id: string) => void;
  onEdit: (id: string, newTitle: string) => void;
  onCategoryChange: (id: string, categoryId: string) => void;
  onUrgencyChange: (id: string, urgencyNumber: number) => void;
  onCreateCategory: (name: string) => Promise<Category | null>;
  onSnooze: (id: string, snoozedUntil: number) => void;
  onWillDoTodayChange: (id: string, willDoToday: 'today' | 'pinned' | false) => void;
  onDescriptionClick: (id: string) => void;
  onManualOrderChange: (id: string, direction: 'up' | 'down') => void;
  onTimerToggle: (id: string) => void;
  onRerank: (id: string) => void;
  source?: {
    type: string;
    url?: string;
    channelId?: string;
    messageId?: string;
  };
  recurrenceDays?: number;
}

export const Task: React.FC<TaskProps> = ({
  id,
  title,
  isCompleted,
  category,
  categories,
  urgency,
  urgencyNumber,
  urgencyRank,
  hasCustomPriority,
  isUnranked,
  willDoToday,
  selectedCategoryId,
  description,
  isFirstInCategory,
  isLastInCategory,
  timerStartedAt,
  totalElapsedTime,
  sharedWith = [],
  sharedBy,
  onDelete,
  onToggle,
  onEdit,
  onCategoryChange,
  onUrgencyChange,
  onCreateCategory,
  onSnooze,
  onWillDoTodayChange,
  onDescriptionClick,
  onManualOrderChange,
  onTimerToggle,
  onRerank,
  source,
  recurrenceDays,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(title);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isCategorySelectOpen, setIsCategorySelectOpen] = useState(false);
  const [isUrgencySelectOpen, setIsUrgencySelectOpen] = useState(false);
  const [isSnoozeDropdownOpen, setIsSnoozeDropdownOpen] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  const [gotLogged, setGotLogged] = useState(false);
  const [appearsChecked, setAppearsChecked] = useState(false);
  const [showWillDoTooltip, setShowWillDoTooltip] = useState(false);
  const [showUpTooltip, setShowUpTooltip] = useState(false);
  const [showDownTooltip, setShowDownTooltip] = useState(false);
  const [hoveredUserEmail, setHoveredUserEmail] = useState<string | null>(null);
  const [pendingUrgencyNumber, setPendingUrgencyNumber] = useState(
    urgencyNumber || 1
  );
  const [hasUrgencyChanges, setHasUrgencyChanges] = useState(false);
  const [currentElapsedTime, setCurrentElapsedTime] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const urgencyDropdownRef = useRef<HTMLDivElement>(null);
  const snoozeDropdownRef = useRef<HTMLDivElement>(null);
  const checkboxRef = useRef<HTMLInputElement>(null);
  const timerIntervalRef = useRef<number | null>(null);

  // Timer effect
  useEffect(() => {
    if (timerStartedAt) {
      // Calculate initial elapsed time
      const now = Date.now();
      const sessionElapsed = Math.floor((now - timerStartedAt) / 1000);
      const total = (totalElapsedTime || 0) + sessionElapsed;
      setCurrentElapsedTime(total);

      // Update timer every second
      timerIntervalRef.current = window.setInterval(() => {
        const now = Date.now();
        const sessionElapsed = Math.floor((now - timerStartedAt) / 1000);
        const total = (totalElapsedTime || 0) + sessionElapsed;
        setCurrentElapsedTime(total);
      }, 1000);

      return () => {
        if (timerIntervalRef.current) {
          window.clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }
      };
    } else {
      // Clear any existing interval
      if (timerIntervalRef.current) {
        window.clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      setCurrentElapsedTime(totalElapsedTime || 0);
    }
  }, [timerStartedAt, totalElapsedTime]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        const target = event.target as HTMLElement;
        if (target.closest('.category-create')) {
          return;
        }
        setIsCategorySelectOpen(false);
        setNewCategoryName('');
        setIsCreatingCategory(false);
      }
      if (
        urgencyDropdownRef.current &&
        !urgencyDropdownRef.current.contains(event.target as Node)
      ) {
        setIsUrgencySelectOpen(false);
      }
      if (
        snoozeDropdownRef.current &&
        !snoozeDropdownRef.current.contains(event.target as Node)
      ) {
        setIsSnoozeDropdownOpen(false);
        setShowDatePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setPendingUrgencyNumber(urgencyNumber || 1);
    setHasUrgencyChanges(false);
  }, [urgencyNumber]);

  const handleEdit = () => {
    if (editedTitle.trim() && editedTitle !== title) {
      onEdit(id, editedTitle);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleEdit();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setEditedTitle(title);
    }
  };

  // Auto-resize textarea to fit content
  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  useEffect(() => {
    if (isEditing) {
      // Small delay to ensure the textarea is rendered
      setTimeout(adjustTextareaHeight, 0);
    }
  }, [isEditing]);

  const handleTitleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditedTitle(e.target.value);
    adjustTextareaHeight();
  };

  const handleCreateCategory = async () => {
    if (newCategoryName.trim() && !isCreatingCategory) {
      setIsCreatingCategory(true);
      try {
        const newCategory = await onCreateCategory(newCategoryName);
        if (newCategory) {
          onCategoryChange(id, newCategory.id);
          setIsCategorySelectOpen(false);
        }
      } finally {
        setIsCreatingCategory(false);
        setNewCategoryName('');
      }
    }
  };

  const handlePendingUrgencyChange = (value: number) => {
    setPendingUrgencyNumber(value);
    setHasUrgencyChanges(value !== (urgencyNumber || 1));
  };

  const handleAcceptUrgency = () => {
    onUrgencyChange(id, pendingUrgencyNumber);
    setHasUrgencyChanges(false);
    setIsUrgencySelectOpen(false);
  };

  const handleCancelUrgency = () => {
    setPendingUrgencyNumber(urgencyNumber || 1);
    setHasUrgencyChanges(false);
  };

  const handleToggle = () => {
    if (!isCompleted) {
      // Set visual state immediately
      setAppearsChecked(true);
      setGotLogged(true);

      // Delay the actual state change
      setTimeout(() => {
        onToggle(id);
        setGotLogged(false);
      }, 1000);
    } else {
      onToggle(id);
      setAppearsChecked(false);
    }
  };

  const handleSnooze = (date: Date | null) => {
    if (date) {
      onSnooze(id, date.getTime());
    }
    setIsSnoozeDropdownOpen(false);
    setShowDatePicker(false);
  };

  const handleTomorrowSnooze = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    handleSnooze(tomorrow);
  };

  const handleDateSelect = (date: Date | null) => {
    setSelectedDate(date);
  };

  const handleDateSubmit = () => {
    if (selectedDate) {
      handleSnooze(selectedDate);
    }
  };

  const handleTaskBodyClick = (e: React.MouseEvent) => {
    // Don't trigger if clicking on buttons, interactive elements, or the title
    if (
      e.target instanceof HTMLElement &&
      (e.target.closest('button') ||
        e.target.closest('input') ||
        e.target.closest('textarea') ||
        e.target.closest('.task-edit-input') ||
        e.target.closest('.task-category') ||
        e.target.closest('.task-urgency') ||
        e.target.closest('.snooze-btn') ||
        e.target.closest('.timer-btn') ||
        e.target.closest('.task-title') ||
        e.target.closest('.snooze-dropdown'))
    ) {
      return;
    }
    // Also don't trigger if we're in editing mode
    if (isEditing) {
      return;
    }
    onDescriptionClick(id);
  };

  const getServiceIcon = (sourceType: string) => {
    switch (sourceType) {
      case 'slack':
        return '💬'; // You can replace this with an actual Slack icon
      // Add more cases for other services
      default:
        return null;
    }
  };

  const formatElapsedTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatCompactTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds % 60}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const handleTimerToggle = () => {
    onTimerToggle(id);
  };

  // Derive urgency category from urgencyNumber for consistent card styling
  // Thresholds: High >= 8, Medium 4-7.9, Low < 4
  // Note: must check !== undefined because 0 is a valid urgencyNumber but falsy
  const derivedUrgency: 'high' | 'medium' | 'low' | null = urgencyNumber !== undefined
    ? urgencyNumber >= 8
      ? 'high'
      : urgencyNumber >= 4
        ? 'medium'
        : 'low'
    : urgency;

  return (
    <div
      className={`task-card ${appearsChecked || isCompleted ? 'completed' : ''}`}
      data-urgency={derivedUrgency || undefined}
      data-will-do-today={willDoToday || undefined}
      data-selected-category={selectedCategoryId}
      onClick={handleTaskBodyClick}
    >
      <Confetti
        active={gotLogged}
        config={{
          angle: 90,
          spread: 360,
          startVelocity: 40,
          elementCount: 70,
          dragFriction: 0.12,
          duration: 3000,
          stagger: 3,
          width: '10px',
          height: '10px',
          colors: ['#a864fd', '#29cdff', '#78ff44', '#ff718d', '#fdff6a'],
        }}
      />
      <div className="task-content">
        <div className="task-header">
          <div
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <input
              ref={checkboxRef}
              type="checkbox"
              className="task-checkbox"
              checked={appearsChecked || isCompleted}
              onChange={handleToggle}
            />
            <button
              className={`will-do-today-btn ${willDoToday ? 'active' : ''}`}
              onClick={e => {
                e.stopPropagation();
                // Cycle through three states: false -> 'pinned' -> 'today' -> false
                const nextState =
                  willDoToday === false ? 'pinned' :
                  willDoToday === 'pinned' ? 'today' :
                  false;
                onWillDoTodayChange(id, nextState);
              }}
              onMouseEnter={() => setShowWillDoTooltip(true)}
              onMouseLeave={() => setShowWillDoTooltip(false)}
            >
              {willDoToday === 'today' ? <TbClockDown /> : <TbPin />}
              {showWillDoTooltip && (
                <div className="will-do-tooltip">
                  {willDoToday === 'pinned' ? 'Pinned To Top' :
                   willDoToday === 'today' ? 'Doing Today' :
                   'Pin To Top'}
                </div>
              )}
            </button>
          </div>
          {isEditing ? (
            <textarea
              ref={textareaRef}
              className="task-edit-input"
              value={editedTitle}
              onChange={handleTitleChange}
              onBlur={handleEdit}
              onKeyDown={handleKeyDown}
              onClick={e => e.stopPropagation()}
              autoFocus
              rows={1}
            />
          ) : (
            <div
              className="task-title"
              onClick={e => {
                e.stopPropagation();
                setIsEditing(true);
              }}
            >
              {title}
            </div>
          )}
        </div>
        {(!timerStartedAt && currentElapsedTime > 0) || hasCustomPriority || isUnranked ? (
          <div className="task-elapsed-time">
            {!timerStartedAt && currentElapsedTime > 0 && (
              <span>Time spent: {formatCompactTime(currentElapsedTime)}</span>
            )}
            {hasCustomPriority && (
              <span className="custom-priority-badge">Custom Priority</span>
            )}
            {isUnranked && (
              <span className="unranked-badge">Unranked</span>
            )}
          </div>
        ) : null}
        {timerStartedAt && (
          <div className="task-timer-display">
            <span className="timer-label">Timer: </span>
            <span className="timer-time">
              {formatElapsedTime(currentElapsedTime)}
            </span>
          </div>
        )}
        <div className="task-meta">
          <div
            className="task-category"
            onClick={() => setIsCategorySelectOpen(!isCategorySelectOpen)}
          >
            {category}
            {isCategorySelectOpen && (
              <div className="task-category-select" ref={dropdownRef}>
                <div className="category-options">
                  {categories.map(cat => (
                    <div
                      key={cat.id}
                      className="category-option"
                      onClick={() => {
                        onCategoryChange(id, cat.id);
                        setIsCategorySelectOpen(false);
                      }}
                    >
                      {cat.name}
                    </div>
                  ))}
                  <div className="category-create">
                    <input
                      type="text"
                      value={newCategoryName}
                      onChange={e => setNewCategoryName(e.target.value)}
                      placeholder="New category..."
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          handleCreateCategory();
                        }
                      }}
                      onClick={e => e.stopPropagation()}
                    />
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        handleCreateCategory();
                      }}
                      disabled={isCreatingCategory || !newCategoryName.trim()}
                    >
                      {isCreatingCategory ? 'Creating...' : 'Create'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          {description && <TbNotes className="note-icon" size={20} />}
          {recurrenceDays && (
            <div
              className="recurrence-indicator"
              title={`Repeats every ${recurrenceDays} day${recurrenceDays > 1 ? 's' : ''}`}
            >
              <TbRepeat size={14} />
              <span>{recurrenceDays}d</span>
            </div>
          )}
          {source && (
            <a
              href={source.url}
              target="_blank"
              rel="noopener noreferrer"
              className="task-source-icon"
              title={`Created from ${source.type}`}
            >
              {getServiceIcon(source.type)}
            </a>
          )}
          {sharedWith && sharedWith.length > 0 && (
            <div className="shared-users-container">
              {sharedWith.slice(0, 3).map((user, index) => (
                <div
                  key={user.userId}
                  className="shared-user-circle"
                  style={{ marginLeft: index > 0 ? '-8px' : '0' }}
                  onMouseEnter={() => setHoveredUserEmail(user.email)}
                  onMouseLeave={() => setHoveredUserEmail(null)}
                >
                  {user.photoURL ? (
                    <img src={user.photoURL} alt={user.email} className="shared-user-avatar" />
                  ) : (
                    <div className="shared-user-initials">
                      {user.email.charAt(0).toUpperCase()}
                    </div>
                  )}
                  {hoveredUserEmail === user.email && (
                    <div className="shared-user-tooltip">{user.email}</div>
                  )}
                </div>
              ))}
              {sharedWith.length > 3 && (
                <div className="shared-user-count">
                  +{sharedWith.length - 3}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="task-actions-top">
        <button
          className="delete-btn"
          onClick={() => onDelete(id)}
          title={sharedBy ? "Leave task" : "Delete task"}
        >
          ×
        </button>
      </div>
      <div className="task-actions-bottom">
        <div className="manual-order-controls">
          <button
            className="manual-order-btn manual-order-up"
            onClick={e => {
              e.stopPropagation();
              onManualOrderChange(id, 'up');
            }}
            onMouseEnter={() => setShowUpTooltip(true)}
            onMouseLeave={() => setShowUpTooltip(false)}
            title="Bump Higher"
            // disabled={isFirstInCategory}
          >
            <TbArrowUp />
            {showUpTooltip && (
              <div className="manual-order-tooltip">Bump Higher</div>
            )}
          </button>
          <button
            className="manual-order-btn manual-order-down"
            onClick={e => {
              e.stopPropagation();
              onManualOrderChange(id, 'down');
            }}
            onMouseEnter={() => setShowDownTooltip(true)}
            onMouseLeave={() => setShowDownTooltip(false)}
            title="Bump Lower"
            // disabled={isLastInCategory}
          >
            <TbArrowDown />
            {showDownTooltip && (
              <div className="manual-order-tooltip">Bump Lower</div>
            )}
          </button>
        </div>
        {(urgency || urgencyNumber) && (
          <div
            className="task-urgency-container"
            ref={urgencyDropdownRef}
            onClick={e => e.stopPropagation()}
          >
            <span
              className="task-urgency"
              onClick={() => setIsUrgencySelectOpen(!isUrgencySelectOpen)}
            >
              <span
                style={{
                  fontWeight: 600,
                  color:
                    (urgencyNumber ?? 1) >= 8
                      ? 'rgb(250, 82, 82)'
                      : (urgencyNumber ?? 1) >= 4
                        ? 'rgb(253, 126, 20)'
                        : 'rgb(134, 142, 150)',
                }}
              >
                {urgencyNumber?.toFixed(1)}
              </span>
            </span>
            {isUrgencySelectOpen && (
              <div className="urgency-select-dropdown">
                <Slider
                  min={0}
                  max={10}
                  step={1}
                  value={pendingUrgencyNumber}
                  onChange={handlePendingUrgencyChange}
                  size="xl"
                  style={{
                    width: '100%',
                    marginTop: 4,
                    marginRight: 5,
                  }}
                  label={`${
                    pendingUrgencyNumber >= 8
                      ? 'High Urgency ❗️'
                      : pendingUrgencyNumber >= 4
                        ? 'Medium Urgency ⚠️'
                        : pendingUrgencyNumber === 0
                          ? 'No Urgency'
                          : 'Low Urgency ℹ️'
                  }`}
                  color={
                    pendingUrgencyNumber >= 8
                      ? 'red'
                      : pendingUrgencyNumber >= 4
                        ? 'orange'
                        : 'gray'
                  }
                  thumbChildren={
                    <span style={{ fontWeight: 600 }}>
                      {pendingUrgencyNumber}
                    </span>
                  }
                  styles={{
                    thumb: {
                      borderWidth: 2,
                      borderColor:
                        pendingUrgencyNumber >= 8
                          ? 'red'
                          : pendingUrgencyNumber >= 4
                            ? 'orange'
                            : 'gray',
                      padding: 11,
                    },
                    label: {
                      fontSize: '14px',
                      fontWeight: 500,
                      backgroundColor:
                        pendingUrgencyNumber >= 8
                          ? 'rgb(250, 82, 82)'
                          : pendingUrgencyNumber >= 4
                            ? 'rgb(253, 126, 20)'
                            : 'rgb(134, 142, 150)',
                    },
                  }}
                />
                <button
                  className="rerank-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsUrgencySelectOpen(false);
                    onRerank(id);
                  }}
                  type="button"
                >
                  {isUnranked || hasCustomPriority ? 'Rank' : 'Re-rank'}
                </button>
                {hasUrgencyChanges && (
                  <div
                    className="urgency-actions"
                    style={{ marginTop: '12px', display: 'flex', gap: '8px' }}
                  >
                    <button
                      className="urgency-accept-btn"
                      onClick={handleAcceptUrgency}
                      style={{
                        padding: '5px 14px',
                        fontSize: '15px',
                        fontWeight: 500,
                        backgroundColor: '#4eb944',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                      }}
                    >
                      Update
                    </button>
                    <button
                      className="urgency-cancel-btn"
                      onClick={handleCancelUrgency}
                      style={{
                        padding: '5px 14px',
                        fontSize: '15px',
                        fontWeight: 500,
                        backgroundColor: '#868e96',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <div
          className="snooze-container"
          ref={snoozeDropdownRef}
          onClick={e => e.stopPropagation()}
        >
          <button
            className="snooze-btn"
            onClick={() => setIsSnoozeDropdownOpen(!isSnoozeDropdownOpen)}
          >
            ⏰
          </button>
          {isSnoozeDropdownOpen && (
            <div className="snooze-dropdown">
              <button className="snooze-option" onClick={handleTomorrowSnooze}>
                Tomorrow
              </button>
              <button
                className="snooze-option"
                onClick={() => setShowDatePicker(!showDatePicker)}
              >
                Select date
              </button>
              {showDatePicker && (
                <div className="date-picker-container">
                  <DatePicker
                    value={selectedDate}
                    onChange={handleDateSelect}
                    minDate={(() => {
                      const tomorrow = new Date();
                      tomorrow.setDate(tomorrow.getDate() + 1);
                      return tomorrow;
                    })()}
                    className="date-picker"
                    firstDayOfWeek={0}
                  />
                  <button
                    className="date-submit-btn"
                    onClick={handleDateSubmit}
                    disabled={!selectedDate}
                  >
                    Confirm
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        <button
          className="timer-btn"
          onClick={e => {
            e.stopPropagation();
            handleTimerToggle();
          }}
          title={timerStartedAt ? 'Stop timer' : 'Start timer'}
        >
          {/*{timerStartedAt ? <IoStopOutline /> : <IoPlayOutline />}*/}
          {timerStartedAt ? '⏹️' : '▶️'}
        </button>
      </div>
    </div>
  );
};
